import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { getMyInstances, getMyTicketAccounts } from '@/apis/labReservation';
import { TicketAccountStatus } from '@/constants/lab';

const useLabStore = defineStore('lab', () => {
    const myEnvironments = ref<LabMyInstance[]>([]);
    const ticketActivities = ref<LabTicketAccountActivity[]>([]);
    const isCoordinator = ref(false);
    const coordinatorActivityIds = ref<string[]>([]);
    const environmentsLoading = ref(false);
    const ticketsLoading = ref(false);

    // 活动类型在券账户的活动层，实例列表里没有 source_type
    const hasTrainingEnv = computed(() => ticketActivities.value.some(a => a.activity_type === 'training'));

    // 每个活动下的字段叫 environments，不是 accounts；状态字段是 ticket_status
    const activeTicketAccounts = computed(() =>
        ticketActivities.value
            .flatMap(a => a.environments || [])
            .filter(acc => acc.ticket_status === TicketAccountStatus.Normal)
    );

    const fetchMyEnvironments = async (params?: { activity_id?: string; status?: string; page?: number; page_size?: number }) => {
        environmentsLoading.value = true;
        try {
            const res = await getMyInstances(params);
            myEnvironments.value = res.data.items;
            return res;
        } catch (e) {
            myEnvironments.value = [];
            throw e;
        } finally {
            environmentsLoading.value = false;
        }
    };

    const fetchMyTicketAccounts = async (activityId?: string) => {
        ticketsLoading.value = true;
        try {
            const res = await getMyTicketAccounts(activityId ? { activity_id: activityId } : undefined);
            ticketActivities.value = res.data.activities || [];
            return res;
        } catch (e) {
            ticketActivities.value = [];
            throw e;
        } finally {
            ticketsLoading.value = false;
        }
    };

    const setCoordinatorStatus = (coordinator: boolean, activityIds: string[]) => {
        isCoordinator.value = coordinator;
        coordinatorActivityIds.value = activityIds;
    };

    const clearLabState = () => {
        myEnvironments.value = [];
        ticketActivities.value = [];
        isCoordinator.value = false;
        coordinatorActivityIds.value = [];
    };

    return {
        myEnvironments,
        ticketActivities,
        isCoordinator,
        coordinatorActivityIds,
        environmentsLoading,
        ticketsLoading,
        hasTrainingEnv,
        activeTicketAccounts,
        fetchMyEnvironments,
        fetchMyTicketAccounts,
        setCoordinatorStatus,
        clearLabState,
    };
});

export default useLabStore;
